import { useEffect, useRef, useState } from 'preact/hooks'
import type { NetState } from '../../net/GameClient'
import type { Flow } from '../flow'
import type { RoomInfo, Session } from '../session'
import { useStore } from '../useStore'
import { HANDOFF_MS, handoffView } from './handoff'
import { Matchmaking } from './Matchmaking'

interface Props {
  session: Session
  flow: Flow
  room: RoomInfo | null
  net: NetState
  emotes: (number | null)[]
}

/**
 * The matchmaking screen on the `lobby` screen, and over the `race` screen's first moment
 * (PHASE3 T3.2, handoff.ts): the bots drop onto the empty pedestals under "Get ready!", and
 * "Loading…" stays until the race scene is built, then the race shows. `App` renders this at
 * the same place for both screens, so the humans already standing do not drop in again.
 */
export function MatchmakingLayer({ session, flow, room, net, emotes }: Props) {
  const raceReady = useStore(session.raceReady)
  const [, rerender] = useState(0)
  const race = flow.screen === 'race'
  // When the race screen took over, and whether the hand-off is over (one per race).
  const since = useRef<number | null>(null)
  const dismissed = useRef(false)
  if (!race) {
    since.current = null
    dismissed.current = false
  } else if (since.current === null) {
    since.current = performance.now()
  }
  const sinceMs = since.current === null ? 0 : performance.now() - since.current
  const view = race
    ? handoffView({
        phase: room?.phase ?? null,
        sinceMs,
        raceReady,
        dismissed: dismissed.current,
      })
    : null
  if (race && view === null) dismissed.current = true

  useEffect(() => {
    if (view !== 'handoff') return
    const t = setTimeout(() => rerender((n) => n + 1), Math.max(0, HANDOFF_MS - sinceMs) + 16)
    return () => clearTimeout(t)
  })

  if (race && view === null) return null
  return (
    <Matchmaking
      session={session}
      flow={flow}
      room={room}
      net={net}
      emotes={emotes}
      mode={race ? 'handoff' : 'lobby'}
      loading={view === 'loading'}
    />
  )
}
